<template>
  <v-card class="pa-4" elevation="2" rounded="lg">
    <v-row dense>
      <v-col cols="6">
        <v-card class="pa-3 stat-card" outlined rounded="lg">
          <div>
            <div class="orbitron-subtitle">
              Puntos
            </div>

            <div class="text-h6">
              {{ points }}
            </div>
          </div>

          <template v-if="showCountdown">
            <v-divider class="my-2"></v-divider>

            <div>
              <div class="orbitron-subtitle">
                Tiempo restante
              </div>

              <div
                class="text-h6 countdown-value"
                :class="{
                  'countdown-finished':
                    isLeagueExpired
                }"
              >
                {{ countdownText }}
              </div>
            </div>
          </template>
        </v-card>
      </v-col>

      <v-col cols="6">
        <v-card
          class="pa-3 budget-card"
          outlined
          rounded="lg"
        >
          <div>
            <div class="orbitron-subtitle">
              Presupuesto total
            </div>

            <div class="text-h6">
              {{ budgetTotalFormatted }}
            </div>
          </div>

          <v-divider class="my-2"></v-divider>

          <div>
            <div class="orbitron-subtitle">
              Presupuesto disponible
            </div>

            <div
              class="text-h6 available-budget"
              :class="{
                'negative-budget':
                  availableBudget < 0
              }"
            >
              {{ availableBudgetFormatted }}
            </div>
          </div>
        </v-card>
      </v-col>

      <v-col cols="4">
        <v-card
          class="pa-3 stat-card"
          outlined
          rounded="lg"
        >
          <div class="orbitron-subtitle">
            Posición
          </div>

          <div class="text-h6">
            #{{ rank }} de {{ totalPlayers }}
          </div>
        </v-card>
      </v-col>

      <v-col cols="4">
        <v-card
          class="pa-3 stat-card"
          outlined
          rounded="lg"
        >
          <div class="orbitron-subtitle">
            Presupuesto reservado
          </div>

          <div class="text-h6">
            {{ reservedBudgetFormatted }}
          </div>

          <div class="text-caption grey--text">
            Dinero bloqueado
          </div>
        </v-card>
      </v-col>
      <v-col cols="4">
        <v-card
          class="pa-3 stat-card"
          outlined
          rounded="lg"
        >
          <div class="orbitron-subtitle">
            Crédito disponible
          </div>

          <div class="text-h6">
            {{ formatCurrency(spendingCapacity) }}
          </div>

          <div class="text-caption grey--text">
            Límite de crédito: {{ formatCurrency(creditLimit) }}
          </div>
        </v-card>
      </v-col>
      <v-col
        v-if="inDebt"
        cols="12"
      >
        <v-alert
          type="warning"
          dense
          outlined
          class="mb-0"
        >
          Estás utilizando crédito. Mientras tu presupuesto sea negativo,
          no obtendrás puntos diarios.
        </v-alert>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
export default {
  name: "GameStats",

  props: {
    currentDay: {
      type: Number,
      required: true,
    },

    totalDays: {
      type: Number,
      required: true,
    },

    points: {
      type: Number,
      required: true,
    },

    budget: {
      type: Number,
      required: true,
    },

    reservedBudget: {
      type: Number,
      default: 0,
    },

    rank: {
      type: Number,
      required: true,
    },

    totalPlayers: {
      type: Number,
      required: true,
    },

    leagueId: {
      type: [String, Number],
      default: null,
    },

    leagueStatus: {
      type: String,
      default: "",
    },

    leagueEndAt: {
      type: String,
      default: null,
    },
    creditLimit: {
      type: Number,
      default: 0,
    },

    spendingCapacity: {
      type: Number,
      default: 0,
    },

    inDebt: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      now: Date.now(),
      countdownTimer: null,
    };
  },

  computed: {
    availableBudget() {
      const total =
        Number(this.budget) || 0;

      const reserved =
        Number(this.reservedBudget) || 0;

      return total - reserved;
    },

    budgetTotalFormatted() {
      return this.formatCurrency(
        this.budget
      );
    },

    reservedBudgetFormatted() {
      return this.formatCurrency(
        this.reservedBudget
      );
    },

    availableBudgetFormatted() {
      return this.formatCurrency(
        this.availableBudget
      );
    },

    showCountdown() {
      return (
        String(this.leagueId || "") !== "1"
      );
    },

    leagueEndTimestamp() {
      if (!this.leagueEndAt) {
        return null;
      }

      const timestamp =
        new Date(
          this.leagueEndAt
        ).getTime();

      if (!Number.isFinite(timestamp)) {
        return null;
      }

      return timestamp;
    },

    remainingMilliseconds() {
      if (
        this.leagueStatus !== "active" ||
        !this.leagueEndTimestamp
      ) {
        return 0;
      }

      return Math.max(
        0,
        this.leagueEndTimestamp -
          this.now
      );
    },

    isLeagueExpired() {
      if (
        this.leagueStatus === "finished"
      ) {
        return true;
      }

      if (
        this.leagueStatus !== "active" ||
        !this.leagueEndTimestamp
      ) {
        return false;
      }

      return (
        this.now >=
        this.leagueEndTimestamp
      );
    },

    countdownText() {
      if (
        this.leagueStatus === "waiting"
      ) {
        return "Sin iniciar";
      }

      if (
        this.leagueStatus === "finished" ||
        this.isLeagueExpired
      ) {
        return "Finalizada";
      }

      if (
        this.leagueStatus !== "active" ||
        !this.leagueEndTimestamp
      ) {
        return "—";
      }

      const totalSeconds =
        Math.floor(
          this.remainingMilliseconds /
            1000
        );

      const days =
        Math.floor(
          totalSeconds / 86400
        );

      const hours =
        Math.floor(
          (totalSeconds % 86400) /
            3600
        );

      const minutes =
        Math.floor(
          (totalSeconds % 3600) /
            60
        );

      const seconds =
        totalSeconds % 60;

      const pad = (value) =>
        String(value).padStart(
          2,
          "0"
        );

      return (
        `${days}d ` +
        `${pad(hours)}:` +
        `${pad(minutes)}:` +
        `${pad(seconds)}`
      );
    },
  },

  mounted() {
    this.now = Date.now();

    this.countdownTimer =
      window.setInterval(
        () => {
          this.now =
            Date.now();
        },
        1000
      );
  },

  beforeDestroy() {
    if (
      this.countdownTimer
    ) {
      window.clearInterval(
        this.countdownTimer
      );

      this.countdownTimer =
        null;
    }
  },

  methods: {
    formatCurrency(value) {
      const amount =
        Number(value) || 0;

      try {
        return new Intl.NumberFormat(
          "es-ES",
          {
            style: "currency",
            currency: "EUR",
            maximumFractionDigits: 0,
          }
        ).format(amount);
      } catch (e) {
        return `${amount} €`;
      }
    },
  },
};
</script>

<style scoped>
.stat-card,
.budget-card {
  height: 100%;
}

.orbitron-subtitle {
  font-family: "Orbitron", sans-serif !important;
  font-size: 14px;
  font-weight: 500;
  color: rgb(92, 92, 92);
}

.countdown-value {
  font-family: "Orbitron", sans-serif !important;
  font-size: 18px !important;
  color: #31635f;
  white-space: nowrap;
}

.countdown-finished {
  color: #757575 !important;
}

.available-budget {
  color: #2e7d32;
}

.negative-budget {
  color: #c62828;
}

@media (max-width: 600px) {
  .countdown-value {
    font-size: 13px !important;
  }
}
</style>